import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  inject,
  signal,
  Signal,
  viewChild,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngxs/store';
import { isDefined, isNil } from '@trimble-gcs/common';
import { FeatureFlagService } from '@trimble-gcs/feature-flags';
import { TranslationService } from '@trimble-gcs/i18n';
import {
  ModusAutocomplete,
  ModusAutocompleteModule,
  ModusButtonModule,
  ModusIconModule,
  ModusTooltipModule,
} from '@trimble-gcs/modus';
import { combineLatest, filter, map, Observable, switchMap, tap } from 'rxjs';
import { ClearError } from '../../error-handling/error.actions';
import { ErrorState } from '../../error-handling/error.state';
import { FeatureFlagKey } from '../../feature-flags/feature-flag-key';
import { modusOptionListTranslations } from '../../i18n/modus-option-list.translations';
import { LoadingService } from '../../loading/loading.service';
import { GroupRole, Project, ProjectUpdate, UserRole } from '../../tenancy/tenancy.models';
import { TenancyService } from '../../tenancy/tenancy.service';
import { UserGroup } from '../../user-group/user-group.models';
import { UserGroupService } from '../../user-group/user-group.service';
import { Role, Status, User } from '../../user/user.models';
import { UserService } from '../../user/user.service';
import { permissionsTranslations } from './permissions.translations';

const NoRole = 'NONE';
type SelectableRole = Role | typeof NoRole;

interface RoleOption {
  role: SelectableRole;
  displayName: string;
}

interface GroupRoleAssignment extends UserGroup {
  userLabel: string;
  formGroup: FormGroup<{
    id: FormControl<string>;
    role: FormControl<SelectableRole>;
  }>;
}

interface UserRoleAssignment extends UserRole {
  displayValue: string;
  userIsRemoved: boolean;
  formGroup: FormGroup<{
    tidUuid: FormControl<string>;
    role: FormControl<SelectableRole>;
  }>;
}

interface MemberView extends User {
  memberDisplayValue: string;
  roleDisplayName: string;
  isRemoved: boolean;
}

interface PermissionsData {
  project: Project;
  groups: UserGroup[];
  users: User[];
}

@UntilDestroy()
@Component({
  selector: 'sd-config-permissions',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatProgressBarModule,
    MatSelectModule,
    MatRadioModule,
    ModusAutocompleteModule,
    ModusButtonModule,
    ModusIconModule,
    ModusTooltipModule,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './permissions.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PermissionsComponent implements AfterViewInit {
  private readonly loadingService = inject(LoadingService);
  private readonly tenancyService = inject(TenancyService);
  private readonly translationService = inject(TranslationService);
  private readonly store = inject(Store);
  private readonly userGroupService = inject(UserGroupService);
  private readonly userService = inject(UserService);
  private readonly featureFlagService: FeatureFlagService<FeatureFlagKey> =
    inject(FeatureFlagService);

  readonly translations = { ...permissionsTranslations(), ...modusOptionListTranslations() };

  // permissions data
  private permissionsData = signal<PermissionsData | null>(null);

  // errors
  private readonly loadError = this.store.selectSignal(ErrorState.hasError('projectLoadError'));
  private readonly saveError = this.store.selectSignal(ErrorState.hasError('projectSaveError'));

  readonly hasError = computed(() => this.loadError() || this.saveError());

  readonly errorMessage = computed(() => {
    if (this.loadError()) return this.translations.errors.load.text;
    if (this.saveError()) return this.translations.errors.save.text;
    return '';
  });

  // roles
  readonly defaultRoleControl = new FormControl<Role | null>(null);
  readonly defaultRoleOptions = this.getDefaultRoleOptions();

  private readonly noRoleView: RoleOption = {
    role: NoRole,
    displayName: this.translationService.translateSelect(
      { select: NoRole.toLowerCase() },
      this.translations.roles,
    ),
  };
  readonly roleOptions = [this.noRoleView, ...this.defaultRoleOptions];

  // groups
  readonly enableGroupRoleFeature = toSignal(
    this.featureFlagService.hasFeature$('tools_settings.permissions.groups'),
    { initialValue: false },
  );
  readonly groupRoleAssignments = computed(() => {
    const permissionsData = this.permissionsData();
    if (isNil(permissionsData)) return [];

    return this.mapToGroupRoleAssignments(permissionsData.project, permissionsData.groups);
  });

  readonly hasGroups = computed(() => this.groupRoleAssignments().length > 0);

  // add user
  readonly enableUserRoleFeature = toSignal(
    this.featureFlagService.hasFeature$('tools_settings.permissions.users'),
    { initialValue: false },
  );

  readonly addUserControl = new FormControl<string | null>(null);

  private readonly addUserControlValue = toSignal(this.addUserControl.valueChanges, {
    initialValue: null,
  });

  readonly addMemberOptions = computed(() => this.getAddMemberOptions());

  private readonly addUserAutocomplete: Signal<ModusAutocomplete<string>> =
    viewChild.required('addUserAutocomplete');

  // assigned users
  readonly userRoleAssignments = computed(() => {
    const permissionsData = this.permissionsData();
    if (isNil(permissionsData)) return [];

    return this.mapToUserRoleAssignments(permissionsData.project, permissionsData.users);
  });
  readonly hasUsers = computed(() => this.userRoleAssignments().length > 0);

  // effective roles
  readonly members = computed(() => {
    const permissionsData = this.permissionsData();
    if (isNil(permissionsData)) return [];

    return this.mapToMemberView(permissionsData.users);
  });

  // component state
  readonly isLoading = toSignal(this.loadingService.isLoading$(this), {
    initialValue: false,
  });
  readonly isDisabled = computed(() => this.isLoading() || this.hasError());

  constructor() {
    this.createFormStateEffect();
    this.subscribeToDefaultRoleValueChanges();
    this.subscribeToGroupRoleValueChanges();
    this.subscribeToUserRoleValueChanges();

    // Clear any previous error state;
    this.store.dispatch([new ClearError('projectLoadError'), new ClearError('projectSaveError')]);

    this.loadData().subscribe();
  }

  ngAfterViewInit() {
    this.setupAddUserAutocomplete();
  }

  addUser(userTid: string) {
    this.addUserControl.reset();

    const assignedUserRoles = this.userRoleAssignments()
      .map((userRole) => userRole.formGroup.getRawValue())
      .filter((value): value is UserRole => {
        return value.role !== NoRole && value.tidUuid !== userTid;
      });

    const userRoles: UserRole[] = [...assignedUserRoles, { tidUuid: userTid, role: Role.ReadOnly }];

    this.updateProject({ userRoles }).subscribe();
  }

  removeUser(userTid: string) {
    const userRoles = this.userRoleAssignments()
      .map((userRole) => userRole.formGroup.getRawValue())
      .filter((value): value is UserRole => {
        return value.role !== NoRole && value.tidUuid !== userTid;
      });

    this.updateProject({ userRoles }).subscribe();
  }

  private createFormStateEffect(): void {
    effect(() => {
      const disabled = this.isDisabled();
      const groupRoleControls = this.groupRoleAssignments().flatMap(
        (groupRole) => groupRole.formGroup,
      );
      const userRoleControls = this.userRoleAssignments()
        .filter((userRoleAssignment) => !userRoleAssignment.userIsRemoved)
        .flatMap((userRole) => userRole.formGroup);

      const controls = [
        this.defaultRoleControl,
        ...groupRoleControls,
        this.addUserControl,
        ...userRoleControls,
      ];

      if (disabled) {
        controls.forEach((control) => control.disable({ emitEvent: false }));
      } else {
        controls.forEach((control) => control.enable({ emitEvent: false }));
      }
    });
  }

  private subscribeToDefaultRoleValueChanges(): void {
    this.defaultRoleControl.valueChanges
      .pipe(
        filter(isDefined),
        switchMap((role) => this.updateProject({ defaultUserRole: role })),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private subscribeToGroupRoleValueChanges(): void {
    toObservable(this.groupRoleAssignments)
      .pipe(
        map((groupRoleAssignments) => {
          return new FormArray(
            groupRoleAssignments.map((groupRoleAssignment) => groupRoleAssignment.formGroup),
          );
        }),
        switchMap((formArray) => formArray.valueChanges.pipe(map(() => formArray.getRawValue()))),
        map((values) => values.filter((value): value is GroupRole => value.role !== NoRole)),
        switchMap((groupRoles) => this.updateProject({ groupRoles })),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private subscribeToUserRoleValueChanges(): void {
    toObservable(this.userRoleAssignments)
      .pipe(
        map((userRoleAssignments) => {
          return new FormArray(
            userRoleAssignments.map((userRoleAssignment) => userRoleAssignment.formGroup),
          );
        }),
        switchMap((formArray) => formArray.valueChanges.pipe(map(() => formArray.getRawValue()))),
        map((values) => values.filter((value): value is UserRole => value.role !== NoRole)),
        switchMap((userRoles) => this.updateProject({ userRoles })),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private loadData(): Observable<PermissionsData> {
    const project$ = this.tenancyService.getProject();
    const groupRoles$ = this.userGroupService.getUserGroups();
    const users$ = this.userService
      .getUsers()
      .pipe(map((users) => users.filter((user) => isDefined(user.id))));

    const fetchData$ = combineLatest([project$, groupRoles$, users$]);

    return this.loadingService.loadFrom(fetchData$, this).pipe(
      map(([project, groups, users]) => ({ project, groups, users })),
      tap((PermissionsData) => {
        this.defaultRoleControl.setValue(PermissionsData.project.defaultUserRole, {
          emitEvent: false,
        });
        this.permissionsData.set(PermissionsData);
      }),
    );
  }

  private updateProject(projectUpdate: Partial<ProjectUpdate>): Observable<PermissionsData> {
    const updateProjectAndReloadUsers$ = this.tenancyService
      .updateProject(projectUpdate)
      .pipe(switchMap(() => this.loadData()));

    return this.loadingService.loadFrom(updateProjectAndReloadUsers$, this);
  }

  private setupAddUserAutocomplete() {
    const addUserAutocomplete = this.addUserAutocomplete();

    addUserAutocomplete.inputKeydown$
      .pipe(
        filter((event) => event.key === 'Enter'),
        map(() => {
          const value = this.addUserControl.getRawValue();
          if (isNil(value) || value.length === 0) return null;

          const options = this.getAddMemberOptions();

          const exactMatch = options.find((option) => {
            return option.memberDisplayValue.toLowerCase() === value.toLowerCase();
          });
          if (isDefined(exactMatch)) return exactMatch.id;

          const partialMatch = options.find((option) => {
            return option.memberDisplayValue.toLowerCase().includes(value.toLowerCase());
          });

          return partialMatch?.id ?? null;
        }),
        filter(isDefined),
        untilDestroyed(this),
      )
      .subscribe((userId) => {
        this.addUser(userId);
        this.addUserAutocomplete().closePanel();
      });
  }

  private getDefaultRoleOptions(): RoleOption[] {
    return Object.values(Role)
      .filter((role) => role != Role.Admin)
      .map((role) => {
        const roleDisplayName = this.translationService.translateSelect(
          { select: role.toLowerCase() },
          this.translations.roles,
        );

        return {
          role,
          displayName: roleDisplayName,
        } satisfies RoleOption;
      });
  }

  private getAddMemberOptions(): MemberView[] {
    const users = this.members().filter((user) => !user.isRemoved);
    const filter = this.addUserControlValue();
    const assignedUsers = this.userRoleAssignments() ?? [];
    const availableUsers = users.filter((user) => {
      return !assignedUsers.some((assignedUser) => assignedUser.tidUuid === user.id);
    });

    if (isNil(filter)) return availableUsers;

    const filterValue = filter.toLowerCase();
    return availableUsers.filter((user) =>
      user.memberDisplayValue.toLowerCase().includes(filterValue),
    );
  }

  private mapToGroupRoleAssignments(
    project: Project,
    userGroups: UserGroup[],
  ): GroupRoleAssignment[] {
    const pluralMap = this.translations.groups.users;

    return userGroups.map((userGroup) => {
      const selectedRole =
        project.groupRoles?.find((groupRole) => groupRole.id === userGroup.id)?.role ?? NoRole;

      const userLabel = this.translationService.translatePlural(
        { count: userGroup.usersCount },
        pluralMap,
      );

      return {
        ...userGroup,
        userLabel,
        formGroup: new FormGroup({
          id: new FormControl<string>(userGroup.id, { nonNullable: true }),
          role: new FormControl<SelectableRole>(selectedRole, { nonNullable: true }),
        }),
      } satisfies GroupRoleAssignment;
    });
  }

  private mapToUserRoleAssignments(project: Project, users: User[]): UserRoleAssignment[] {
    const userRoles = project.userRoles ?? [];
    return userRoles
      .map((userRole) => {
        const user = users.find((user) => user.id === userRole.tidUuid);
        if (isNil(user)) return null;

        const role = user.status === Status.Removed ? Role.NoAccess : userRole.role;
        const userRoleAssignment: UserRoleAssignment = {
          ...userRole,
          displayValue: this.getUserDisplayName(user),
          userIsRemoved: user.status === Status.Removed,
          formGroup: new FormGroup({
            tidUuid: new FormControl<string>(userRole.tidUuid, { nonNullable: true }),
            role: new FormControl<SelectableRole>(role, { nonNullable: true }),
          }),
        };

        if (userRoleAssignment.userIsRemoved) userRoleAssignment.formGroup.disable();

        return userRoleAssignment;
      })
      .filter(isDefined);
  }

  private mapToMemberView(users: User[]): MemberView[] {
    return users
      .map((user) => {
        const roleDisplayName = this.translationService.translateSelect(
          { select: user.role.toLowerCase() },
          this.translations.roles,
        );

        return {
          ...user,
          memberDisplayValue: this.getUserDisplayName(user),
          roleDisplayName,
          isRemoved: user.status === Status.Removed,
        };
      })
      .toSorted((a, b) => a.memberDisplayValue.localeCompare(b.memberDisplayValue));
  }

  private getUserDisplayName(user: User): string {
    return `${user.firstName} ${user.lastName} (${user.email})`;
  }
}
